import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getMyOrders } from "../services/orderService";
import PhotoPicker from "../components/common/PhotoPicker";
import { changePassword } from "../services/authService";
import { friendlyError } from "../api/api";
import { formatMoney } from "../utils/money";

export default function Account() {
  const { user, logout, updateProfile, updatePhoto } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({ name: user?.name || "", email: user?.email || "", phone: user?.phone || "" });
  // Separate from `form` — photo saves through a different endpoint
  // (PUT /auth/me/photo) than name/email/phone (PATCH /auth/me), but both
  // are triggered from the same Save Changes click below.
  const [photo, setPhoto] = useState(user?.photo || "");
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");
  const [saveError, setSaveError] = useState("");

  const [spendStatus, setSpendStatus] = useState("loading"); // loading | ok | partial | error
  const [totalSpent, setTotalSpent] = useState(0);
  const [orderCount, setOrderCount] = useState(0);

  useEffect(() => {
    // "Total spending" is not a backend-provided figure — there is no such
    // endpoint. This sums `total` across the customer's own orders
    // (already correctly scoped server-side by the backend), not a
    // frontend-invented number pulled from thin air.
    getMyOrders({ page: 1, limit: 200 })
      .then((res) => {
        const sum = res.items.reduce((s, o) => s + Number(o.total || 0), 0);
        setTotalSpent(sum);
        setOrderCount(res.total);
        setSpendStatus(res.total_pages > 1 ? "partial" : "ok");
      })
      .catch(() => setSpendStatus("error"));
  }, []);

  const nameFieldsChanged =
    form.name !== (user?.name || "") || form.email !== (user?.email || "") || form.phone !== (user?.phone || "");
  const photoChanged = photo !== (user?.photo || "");
  const dirty = nameFieldsChanged || photoChanged;

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaveMessage("");
    setSaveError("");
    try {
      // Two independent backend calls (existing profile PATCH, existing
      // photo PUT) behind one Save Changes click — neither is invented
      // here, and photo/name-email-phone remain fully independent: saving
      // only a new photo never touches name/email/phone and vice versa.
      if (nameFieldsChanged) await updateProfile(form);
      if (photoChanged) await updatePhoto(photo || null);
      setSaveMessage("Profile updated.");
    } catch (err) {
      const msg = err?.response?.data?.detail;
      setSaveError(typeof msg === "string" ? msg : "Unable to update profile.");
    } finally {
      setSaving(false);
    }
  };

  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [pwBusy, setPwBusy] = useState(false);
  const [pwMessage, setPwMessage] = useState("");
  const [pwError, setPwError] = useState("");

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwMessage("");
    setPwError("");
    if (pw.next !== pw.confirm) {
      setPwError("New password and confirmation do not match.");
      return;
    }
    setPwBusy(true);
    try {
      await changePassword({ currentPassword: pw.current, newPassword: pw.next });
      setPw({ current: "", next: "", confirm: "" });
      setPwMessage("Password changed.");
    } catch (err) {
      // Backend messages (wrong current password, policy rules) are safe to show.
      setPwError(friendlyError(err, "Unable to change password. Please try again."));
    } finally {
      setPwBusy(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-8 space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-neutral-900">Account</h1>
        <button onClick={handleLogout} className="text-sm text-neutral-500 hover:text-red-600">
          Logout
        </button>
      </div>

      <div className="rounded-xl border border-neutral-200 bg-white p-5">
        <h2 className="text-sm font-medium text-neutral-900 mb-4">Profile</h2>

        <div className="mb-6 pb-6 border-b border-neutral-100">
          <PhotoPicker name={form.name || user?.name} photo={photo} onChange={setPhoto} disabled={saving} collapsed />
          <div className="text-center mt-3">
            <p className="text-sm font-medium text-neutral-900">{user?.name}</p>
            <p className="text-xs text-neutral-500">{user?.email}</p>
          </div>
        </div>

        <form onSubmit={handleSave} className="space-y-4">
          <div>
            <label htmlFor="account-name" className="block text-sm font-medium text-neutral-700 mb-1">Full Name</label>
            <input
              id="account-name"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-300"
            />
          </div>
          <div>
            <label htmlFor="account-email" className="block text-sm font-medium text-neutral-700 mb-1">Email</label>
            <input
              id="account-email"
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-300"
            />
          </div>
          <div>
            <label htmlFor="account-phone" className="block text-sm font-medium text-neutral-700 mb-1">Phone</label>
            <input
              id="account-phone"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-300"
            />
          </div>
          {saveMessage && <p className="text-sm text-green-700">{saveMessage}</p>}
          {saveError && <p className="text-sm text-red-600">{saveError}</p>}
          <button
            type="submit"
            disabled={saving || !dirty}
            className="bg-neutral-900 text-white text-sm font-medium rounded-lg px-4 py-2.5 hover:bg-neutral-800 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </form>
      </div>

      <div className="rounded-xl border border-neutral-200 bg-white p-5">
        <h2 className="text-sm font-medium text-neutral-900 mb-3">Orders</h2>
        {spendStatus === "loading" && <p className="text-sm text-neutral-500">Loading...</p>}
        {spendStatus === "error" && (
          <p className="text-sm text-neutral-500">Order summary is currently unavailable.</p>
        )}
        {(spendStatus === "ok" || spendStatus === "partial") && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-neutral-500">
              {orderCount} order{orderCount === 1 ? "" : "s"}
              {spendStatus === "partial" && " (showing first 200)"}
            </span>
            <span className="font-medium text-neutral-900">{formatMoney(totalSpent)} total</span>
          </div>
        )}
        <Link to="/orders" className="mt-3 inline-block text-sm text-neutral-600 underline">
          View Order History
        </Link>
      </div>

      <div className="rounded-xl border border-neutral-200 bg-white p-5">
        <h2 className="text-sm font-medium text-neutral-900 mb-4">Change Password</h2>
        <form onSubmit={handleChangePassword} className="space-y-4">
          {[
            ["current", "pw-current", "Current password", "current-password"],
            ["next", "pw-new", "New password", "new-password"],
            ["confirm", "pw-confirm", "Confirm new password", "new-password"],
          ].map(([key, id, label, auto]) => (
            <div key={key}>
              <label htmlFor={id} className="block text-sm font-medium text-neutral-700 mb-1">{label}</label>
              <input
                id={id}
                type="password"
                required
                autoComplete={auto}
                value={pw[key]}
                onChange={(e) => setPw({ ...pw, [key]: e.target.value })}
                className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-300"
              />
            </div>
          ))}
          {pwMessage && <p className="text-sm text-green-700">{pwMessage}</p>}
          {pwError && <p className="text-sm text-red-600">{pwError}</p>}
          <button
            type="submit"
            disabled={pwBusy}
            className="bg-neutral-900 text-white text-sm font-medium rounded-lg px-4 py-2.5 hover:bg-neutral-800 disabled:opacity-50"
          >
            {pwBusy ? "Saving..." : "Change Password"}
          </button>
        </form>
      </div>

      <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-5">
        <h2 className="text-sm font-medium text-neutral-900 mb-1">Quote Requests</h2>
        <p className="text-sm text-neutral-500">
          Your submitted quote requests are followed up by our team using the contact details you provided. Check a request with your quote number and phone or email on the <Link to="/quote/status" className="underline">quote status page</Link>.
        </p>
      </div>
    </div>
  );
}
